import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Ticket, 
  Calendar, 
  Clock, 
  Gift, 
  ShoppingCart,
  Users,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ChevronRight,
  History,
  Star,
  Truck,
  Search,
  Plus,
  Trash2,
  UserPlus
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const Tickets = () => {
  const { user } = useContext(AuthContext);
  const [games, setGames] = useState([]);
  const [quota, setQuota] = useState(null);
  const [orders, setOrders] = useState([]);
  const [lentTickets, setLentTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState(null);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [gameAvailability, setGameAvailability] = useState(null);
  
  // Colleague search state
  const [colleagueSearch, setColleagueSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedColleague, setSelectedColleague] = useState(null);
  const [borrowTicketsAmount, setBorrowTicketsAmount] = useState(0);
  const [borrowUpgradesAmount, setBorrowUpgradesAmount] = useState(0);
  
  // Order form state
  const [orderForm, setOrderForm] = useState({
    free_tickets: 0,
    free_upgrades: 0,
    purchased_tickets: 0,
    purchased_upgrades: 0,
    delivery_type: 'fach',
    hinterlegt_vorname: '',
    hinterlegt_nachname: '',
    borrowed_from: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [gamesRes, quotaRes, ordersRes, lentRes] = await Promise.all([
        axios.get('/tickets/games'),
        axios.get('/tickets/quota'),
        axios.get('/tickets/orders'),
        axios.get('/tickets/orders/lent')
      ]);
      setGames(gamesRes.data);
      setQuota(quotaRes.data);
      setOrders(ordersRes.data);
      setLentTickets(lentRes.data);
    } catch (error) {
      console.error('Error fetching ticket data:', error);
      if (error.response?.status !== 403) {
        toast.error('Fehler beim Laden der Ticket-Daten');
      }
    } finally {
      setLoading(false);
    }
  };

  const openOrderDialog = async (game) => {
    setSelectedGame(game);
    setOrderForm({
      free_tickets: 0,
      free_upgrades: 0,
      purchased_tickets: 0,
      purchased_upgrades: 0,
      delivery_type: 'fach',
      hinterlegt_vorname: '',
      hinterlegt_nachname: '',
      borrowed_from: []
    });
    // Reset colleague search state
    setColleagueSearch('');
    setSearchResults([]);
    setSelectedColleague(null);
    setBorrowTicketsAmount(0);
    setBorrowUpgradesAmount(0);
    
    try {
      const availRes = await axios.get(`/tickets/games/${game.id}/availability`);
      setGameAvailability(availRes.data);
      setShowOrderDialog(true);
    } catch (error) {
      toast.error('Fehler beim Laden der Verfügbarkeit');
    }
  };

  // Check if borrowing is needed (user exceeds per-game limit)
  const needsBorrowing = () => {
    if (!gameAvailability) return false;
    const maxTickets = gameAvailability.rules.max_free_tickets;
    const maxUpgrades = gameAvailability.rules.max_free_upgrades;
    
    // Calculate how many are already borrowed
    const alreadyBorrowedTickets = orderForm.borrowed_from.reduce((sum, b) => sum + (b.tickets_borrowed || 0), 0);
    const alreadyBorrowedUpgrades = orderForm.borrowed_from.reduce((sum, b) => sum + (b.upgrades_borrowed || 0), 0);
    
    // User needs to borrow if they want more than their own limit
    const ownTicketsNeeded = orderForm.free_tickets - alreadyBorrowedTickets;
    const ownUpgradesNeeded = orderForm.free_upgrades - alreadyBorrowedUpgrades;
    
    return ownTicketsNeeded > maxTickets || ownUpgradesNeeded > maxUpgrades;
  };

  // Calculate how many more tickets/upgrades need to be borrowed
  const getBorrowingNeeded = () => {
    if (!gameAvailability) return { tickets: 0, upgrades: 0 };
    const maxTickets = gameAvailability.rules.max_free_tickets;
    const maxUpgrades = gameAvailability.rules.max_free_upgrades;
    
    const alreadyBorrowedTickets = orderForm.borrowed_from.reduce((sum, b) => sum + (b.tickets_borrowed || 0), 0);
    const alreadyBorrowedUpgrades = orderForm.borrowed_from.reduce((sum, b) => sum + (b.upgrades_borrowed || 0), 0);
    
    const ticketsNeeded = Math.max(0, orderForm.free_tickets - maxTickets - alreadyBorrowedTickets);
    const upgradesNeeded = Math.max(0, orderForm.free_upgrades - maxUpgrades - alreadyBorrowedUpgrades);
    
    return { tickets: ticketsNeeded, upgrades: upgradesNeeded };
  };

  // Search for colleagues
  const handleColleagueSearch = (searchTerm) => {
    setColleagueSearch(searchTerm);
    if (!searchTerm.trim() || !gameAvailability) {
      setSearchResults([]);
      return;
    }
    
    const term = searchTerm.toLowerCase();
    const alreadySelectedIds = orderForm.borrowed_from.map(b => b.from_user_id);
    
    const results = gameAvailability.borrowable_colleagues.filter(c => 
      c.name.toLowerCase().includes(term) && 
      !alreadySelectedIds.includes(c.user_id) &&
      (c.borrowable_tickets > 0 || c.borrowable_upgrades > 0)
    );
    
    setSearchResults(results);
  };

  // Select a colleague to borrow from
  const selectColleague = (colleague) => {
    setSelectedColleague(colleague);
    setBorrowTicketsAmount(0);
    setBorrowUpgradesAmount(0);
    setColleagueSearch('');
    setSearchResults([]);
  };

  // Add colleague to borrowed_from list
  const addBorrowedColleague = () => {
    if (!selectedColleague) return;
    if (borrowTicketsAmount === 0 && borrowUpgradesAmount === 0) {
      toast.error('Bitte mindestens 1 Ticket oder Upgrade auswählen');
      return;
    }
    
    if (borrowTicketsAmount > selectedColleague.borrowable_tickets) {
      toast.error(`${selectedColleague.name} hat nur ${selectedColleague.borrowable_tickets} Tickets verfügbar`);
      return;
    }
    
    if (borrowUpgradesAmount > selectedColleague.borrowable_upgrades) {
      toast.error(`${selectedColleague.name} hat nur ${selectedColleague.borrowable_upgrades} Upgrades verfügbar`);
      return;
    }
    
    setOrderForm({
      ...orderForm,
      borrowed_from: [
        ...orderForm.borrowed_from,
        {
          from_user_id: selectedColleague.user_id,
          from_user_name: selectedColleague.name,
          tickets_borrowed: borrowTicketsAmount,
          upgrades_borrowed: borrowUpgradesAmount
        }
      ]
    });
    
    // Reset selection
    setSelectedColleague(null);
    setBorrowTicketsAmount(0);
    setBorrowUpgradesAmount(0);
  };

  // Remove colleague from borrowed_from list
  const removeBorrowedColleague = (userId) => {
    setOrderForm({
      ...orderForm,
      borrowed_from: orderForm.borrowed_from.filter(b => b.from_user_id !== userId)
    });
  };

  const handleOrder = async () => {
    if (!selectedGame || !gameAvailability) return;
    
    // Validation
    const totalTickets = orderForm.free_tickets + orderForm.purchased_tickets;
    const totalUpgrades = orderForm.free_upgrades + orderForm.purchased_upgrades;
    
    if (totalUpgrades > 0 && totalTickets === 0) {
      toast.error('VIP-Upgrade ist nur mit mindestens einem Ticket möglich');
      return;
    }
    
    if (orderForm.delivery_type === 'hinterlegt') {
      if (!orderForm.hinterlegt_vorname || !orderForm.hinterlegt_nachname) {
        toast.error('Vorname und Nachname sind für "Hinterlegt" erforderlich');
        return;
      }
    }
    
    try {
      await axios.post('/tickets/orders', {
        event_id: selectedGame.id,
        ...orderForm
      });
      
      toast.success('Bestellung erfolgreich erstellt!');
      setShowOrderDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen der Bestellung');
    }
  };

  const cancelOrder = async (orderId) => {
    if (!confirm('Möchten Sie diese Bestellung wirklich stornieren?')) return;
    
    try {
      await axios.post(`/tickets/orders/${orderId}/cancel`);
      toast.success('Bestellung erfolgreich storniert');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Stornieren');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return format(new Date(dateStr), 'dd.MM.yyyy HH:mm', { locale: de });
  };

  const getGameStatus = (game) => {
    const existingOrder = orders.find(o => o.event_id === game.id && !o.is_cancelled);
    if (existingOrder) return 'ordered';
    if (game.ticket_rules?.deadline_passed) return 'deadline';
    if (!game.tickets_enabled) return 'disabled';
    return 'available';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#e30613] border-t-transparent"></div>
      </div>
    );
  }

  if (!quota || quota.error) {
    return (
      <div className="p-6">
        <Card className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Keine aktive Saison</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Es wurde noch keine aktive Ticket-Saison eingerichtet.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tickets-page">
      {/* Quota Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-[#e30613]/10 to-transparent border-[#e30613]/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
              <Ticket className="w-4 h-4" />
              Saison-Kontingent
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-gray-500 mb-2">{quota.season.name}</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-2xl font-bold text-[#e30613]">
                  {quota.remaining.free_tickets}
                </p>
                <p className="text-xs text-gray-500">Tickets verbleibend</p>
                <p className="text-xs text-gray-400">von {quota.quota.free_tickets}</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-600">
                  {quota.remaining.free_upgrades}
                </p>
                <p className="text-xs text-gray-500">Upgrades verbleibend</p>
                <p className="text-xs text-gray-400">von {quota.quota.free_upgrades}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
              <History className="w-4 h-4" />
              Meine Bestellungen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {orders.filter(o => !o.is_cancelled).length}
            </p>
            <p className="text-xs text-gray-500">aktive Bestellungen</p>
            <Button 
              variant="link" 
              size="sm" 
              className="p-0 h-auto mt-2"
              onClick={() => setShowHistoryDialog(true)}
            >
              Historie anzeigen →
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Verliehen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-orange-500">
              {lentTickets.reduce((sum, l) => sum + l.tickets_lent + l.upgrades_lent, 0)}
            </p>
            <p className="text-xs text-gray-500">Tickets/Upgrades verliehen</p>
            {lentTickets.length > 0 && (
              <p className="text-xs text-gray-400 mt-1">
                an {lentTickets.length} Kollegen
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Games List */}
      <div>
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Verfügbare Spiele
        </h2>
        
        {games.length === 0 ? (
          <Card className="text-center py-8">
            <CardContent>
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Keine Spiele verfügbar</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {games.map((game) => {
              const status = getGameStatus(game);
              const existingOrder = orders.find(o => o.event_id === game.id && !o.is_cancelled);
              
              return (
                <Card 
                  key={game.id} 
                  className={`transition-all hover:shadow-md ${
                    status === 'ordered' ? 'border-green-500 bg-green-50/50 dark:bg-green-900/10' :
                    status === 'deadline' || status === 'disabled' ? 'opacity-60' : ''
                  }`}
                  data-testid={`game-card-${game.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{game.title}</CardTitle>
                        <CardDescription className="flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(game.start_date)}
                        </CardDescription>
                      </div>
                      {game.is_special_event && (
                        <Badge variant="outline" className="border-purple-500 text-purple-500">
                          <Star className="w-3 h-3 mr-1" />
                          Sonder-Event
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Max. Tickets:</span>
                        <span className="ml-1 font-medium">{game.max_free_tickets}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Max. Upgrades:</span>
                        <span className="ml-1 font-medium">{game.max_free_upgrades}</span>
                      </div>
                    </div>
                    
                    {game.order_deadline && (
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Deadline: {formatDate(game.order_deadline)}
                      </p>
                    )}
                    
                    <div className="flex gap-2 text-xs">
                      <Badge variant="secondary">
                        Ticket: {game.ticket_price?.toFixed(2) || '0.00'} €
                      </Badge>
                      <Badge variant="secondary">
                        Upgrade: {game.vip_upgrade_price?.toFixed(2) || '0.00'} €
                      </Badge>
                    </div>
                    
                    {status === 'ordered' ? (
                      <div className="flex items-center justify-between pt-2">
                        <Badge className="bg-green-500">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Bestellt
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => cancelOrder(existingOrder.id)}
                          className="text-red-500 hover:text-red-600"
                        >
                          Stornieren
                        </Button>
                      </div>
                    ) : status === 'deadline' ? (
                      <Badge variant="destructive" className="w-full justify-center">
                        <XCircle className="w-3 h-3 mr-1" />
                        Deadline abgelaufen
                      </Badge>
                    ) : status === 'disabled' ? (
                      <Badge variant="secondary" className="w-full justify-center">
                        Tickets deaktiviert
                      </Badge>
                    ) : (
                      <Button 
                        className="w-full bg-[#e30613] hover:bg-[#c00510]"
                        onClick={() => openOrderDialog(game)}
                        data-testid={`order-button-${game.id}`}
                      >
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        Tickets bestellen
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Order Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="w-5 h-5 text-[#e30613]" />
              Tickets bestellen
            </DialogTitle>
            <DialogDescription>
              {selectedGame?.title} - {selectedGame && formatDate(selectedGame.start_date)}
            </DialogDescription>
          </DialogHeader>
          
          {gameAvailability && (
            <div className="space-y-6 mt-4">
              {/* Quota Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Saison-Kontingent Tickets</p>
                  <p className="text-lg font-bold">
                    {gameAvailability.user_quota.remaining.free_tickets} verbleibend
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Saison-Kontingent Upgrades</p>
                  <p className="text-lg font-bold">
                    {gameAvailability.user_quota.remaining.free_upgrades} verbleibend
                  </p>
                </div>
              </div>
              
              {gameAvailability.rules.is_special_event && (
                <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-sm">
                  <Star className="w-4 h-4 inline mr-2 text-purple-500" />
                  <strong>Sonder-Event:</strong> Zählt nicht gegen Ihr Saison-Kontingent
                </div>
              )}

              {/* Free Tickets */}
              <div className="space-y-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <Gift className="w-4 h-4 text-green-500" />
                  Kostenlose Tickets
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>
                      Tickets 
                      <span className="text-gray-400 font-normal ml-1">
                        (Ihr Limit: {gameAvailability.rules.max_free_tickets})
                      </span>
                    </Label>
                    <Input
                      type="number"
                      min={0}
                      value={orderForm.free_tickets}
                      onChange={(e) => setOrderForm({...orderForm, free_tickets: parseInt(e.target.value) || 0})}
                    />
                    {orderForm.free_tickets > gameAvailability.rules.max_free_tickets && (
                      <p className="text-xs text-orange-500 mt-1">
                        Überschreitet Ihr Limit – leihen Sie von Kollegen
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>
                      VIP-Upgrades 
                      <span className="text-gray-400 font-normal ml-1">
                        (Ihr Limit: {gameAvailability.rules.max_free_upgrades})
                      </span>
                    </Label>
                    <Input
                      type="number"
                      min={0}
                      value={orderForm.free_upgrades}
                      onChange={(e) => setOrderForm({...orderForm, free_upgrades: parseInt(e.target.value) || 0})}
                      disabled={orderForm.free_tickets + orderForm.purchased_tickets === 0}
                    />
                    {orderForm.free_upgrades > gameAvailability.rules.max_free_upgrades && (
                      <p className="text-xs text-orange-500 mt-1">
                        Überschreitet Ihr Limit – leihen Sie von Kollegen
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Purchased Tickets */}
              <div className="space-y-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4 text-blue-500" />
                  Zum Kauf
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tickets ({gameAvailability.rules.ticket_price?.toFixed(2)} € pro Stück)</Label>
                    <Input
                      type="number"
                      min={0}
                      value={orderForm.purchased_tickets}
                      onChange={(e) => setOrderForm({...orderForm, purchased_tickets: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>VIP-Upgrades ({gameAvailability.rules.vip_upgrade_price?.toFixed(2)} € pro Stück)</Label>
                    <Input
                      type="number"
                      min={0}
                      value={orderForm.purchased_upgrades}
                      onChange={(e) => setOrderForm({...orderForm, purchased_upgrades: parseInt(e.target.value) || 0})}
                      disabled={orderForm.free_tickets + orderForm.purchased_tickets === 0}
                    />
                  </div>
                </div>
              </div>

              {/* Delivery Type */}
              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <Truck className="w-4 h-4 text-gray-500" />
                  Lieferart
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'fach', label: 'Ins Fach' },
                    { value: 'digital', label: 'Digital' },
                    { value: 'hinterlegt', label: 'Hinterlegt' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setOrderForm({...orderForm, delivery_type: option.value})}
                      className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                        orderForm.delivery_type === option.value
                          ? 'border-[#e30613] bg-[#e30613]/10 text-[#e30613]'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                
                {orderForm.delivery_type === 'hinterlegt' && (
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <Label>Vorname *</Label>
                      <Input
                        value={orderForm.hinterlegt_vorname}
                        onChange={(e) => setOrderForm({...orderForm, hinterlegt_vorname: e.target.value})}
                        placeholder="Vorname"
                      />
                    </div>
                    <div>
                      <Label>Nachname *</Label>
                      <Input
                        value={orderForm.hinterlegt_nachname}
                        onChange={(e) => setOrderForm({...orderForm, hinterlegt_nachname: e.target.value})}
                        placeholder="Nachname"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Borrowing Section - Only show when exceeding per-game limit */}
              {needsBorrowing() && gameAvailability.borrowable_colleagues.length > 0 && (
                <div className="space-y-4 p-4 border-2 border-orange-400 rounded-lg bg-orange-50/50 dark:bg-orange-900/10">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-orange-700 dark:text-orange-400">
                        Pro-Spiel-Limit überschritten
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Sie möchten mehr als Ihr Limit ({gameAvailability.rules.max_free_tickets} Tickets / {gameAvailability.rules.max_free_upgrades} Upgrades).
                        {getBorrowingNeeded().tickets > 0 && (
                          <span className="block mt-1 font-medium text-orange-600">
                            → Noch {getBorrowingNeeded().tickets} Ticket(s) von Kollegen benötigt
                          </span>
                        )}
                        {getBorrowingNeeded().upgrades > 0 && (
                          <span className="block font-medium text-orange-600">
                            → Noch {getBorrowingNeeded().upgrades} Upgrade(s) von Kollegen benötigt
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Already selected colleagues */}
                  {orderForm.borrowed_from.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Ausgewählte Kollegen:</Label>
                      {orderForm.borrowed_from.map((borrowed) => (
                        <div 
                          key={borrowed.from_user_id}
                          className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border"
                        >
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-orange-500" />
                            <span className="font-medium">{borrowed.from_user_name}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-600">
                              {borrowed.tickets_borrowed > 0 && `${borrowed.tickets_borrowed} Tickets`}
                              {borrowed.tickets_borrowed > 0 && borrowed.upgrades_borrowed > 0 && ', '}
                              {borrowed.upgrades_borrowed > 0 && `${borrowed.upgrades_borrowed} Upgrades`}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeBorrowedColleague(borrowed.from_user_id)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Search for colleague */}
                  {!selectedColleague && (getBorrowingNeeded().tickets > 0 || getBorrowingNeeded().upgrades > 0) && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Kollegen suchen:</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          value={colleagueSearch}
                          onChange={(e) => handleColleagueSearch(e.target.value)}
                          placeholder="Name eingeben..."
                          className="pl-10"
                        />
                      </div>
                      
                      {/* Search results */}
                      {searchResults.length > 0 && (
                        <div className="border rounded-lg overflow-hidden bg-white dark:bg-gray-800">
                          {searchResults.map((colleague) => (
                            <button
                              key={colleague.user_id}
                              type="button"
                              onClick={() => selectColleague(colleague)}
                              className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b last:border-b-0 text-left"
                            >
                              <div className="flex items-center gap-2">
                                <UserPlus className="w-4 h-4 text-green-500" />
                                <span>{colleague.name}</span>
                              </div>
                              <span className="text-sm text-gray-500">
                                {colleague.borrowable_tickets}T / {colleague.borrowable_upgrades}U verfügbar
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {colleagueSearch && searchResults.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-2">
                          Kein Kollege gefunden oder alle bereits ausgewählt
                        </p>
                      )}
                    </div>
                  )}

                  {/* Selected colleague - amount input */}
                  {selectedColleague && (
                    <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border-2 border-orange-300">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Users className="w-5 h-5 text-orange-500" />
                          <span className="font-semibold">{selectedColleague.name}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedColleague(null)}
                          className="text-gray-500"
                        >
                          Abbrechen
                        </Button>
                      </div>
                      
                      <p className="text-sm text-gray-500 mb-3">
                        Verfügbar: {selectedColleague.borrowable_tickets} Tickets, {selectedColleague.borrowable_upgrades} Upgrades
                      </p>
                      
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <Label className="text-sm">Tickets leihen</Label>
                          <Input
                            type="number"
                            min={0}
                            max={selectedColleague.borrowable_tickets}
                            value={borrowTicketsAmount}
                            onChange={(e) => setBorrowTicketsAmount(Math.min(parseInt(e.target.value) || 0, selectedColleague.borrowable_tickets))}
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Upgrades leihen</Label>
                          <Input
                            type="number"
                            min={0}
                            max={selectedColleague.borrowable_upgrades}
                            value={borrowUpgradesAmount}
                            onChange={(e) => setBorrowUpgradesAmount(Math.min(parseInt(e.target.value) || 0, selectedColleague.borrowable_upgrades))}
                          />
                        </div>
                      </div>
                      
                      <Button
                        onClick={addBorrowedColleague}
                        className="w-full bg-orange-500 hover:bg-orange-600"
                        disabled={borrowTicketsAmount === 0 && borrowUpgradesAmount === 0}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Hinzufügen
                      </Button>
                    </div>
                  )}

                  {/* Info about borrowing */}
                  <p className="text-xs text-gray-500 mt-2">
                    <strong>Hinweis:</strong> Geliehene Tickets werden von Ihrem Saison-Kontingent abgezogen. 
                    Der Kollege verliert keine Tickets – nur seine Abrufmöglichkeit für dieses Spiel wird reduziert.
                  </p>
                </div>
              )}

              {/* Summary */}
              <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <h4 className="font-semibold mb-2">Zusammenfassung</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Kostenlose Tickets:</span>
                    <span className="font-medium">{orderForm.free_tickets}</span>
                  </div>
                  {orderForm.borrowed_from.length > 0 && (
                    <div className="flex justify-between text-orange-600">
                      <span className="pl-4">↳ davon geliehen:</span>
                      <span className="font-medium">
                        {orderForm.borrowed_from.reduce((sum, b) => sum + b.tickets_borrowed, 0)} Tickets
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Kostenlose Upgrades:</span>
                    <span className="font-medium">{orderForm.free_upgrades}</span>
                  </div>
                  {orderForm.borrowed_from.some(b => b.upgrades_borrowed > 0) && (
                    <div className="flex justify-between text-orange-600">
                      <span className="pl-4">↳ davon geliehen:</span>
                      <span className="font-medium">
                        {orderForm.borrowed_from.reduce((sum, b) => sum + b.upgrades_borrowed, 0)} Upgrades
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Tickets zum Kauf:</span>
                    <span className="font-medium">
                      {orderForm.purchased_tickets} × {gameAvailability.rules.ticket_price?.toFixed(2)} € = {(orderForm.purchased_tickets * (gameAvailability.rules.ticket_price || 0)).toFixed(2)} €
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Upgrades zum Kauf:</span>
                    <span className="font-medium">
                      {orderForm.purchased_upgrades} × {gameAvailability.rules.vip_upgrade_price?.toFixed(2)} € = {(orderForm.purchased_upgrades * (gameAvailability.rules.vip_upgrade_price || 0)).toFixed(2)} €
                    </span>
                  </div>
                  <hr className="my-2" />
                  <div className="flex justify-between font-bold">
                    <span>Zu zahlen:</span>
                    <span className="text-[#e30613]">
                      {(
                        orderForm.purchased_tickets * (gameAvailability.rules.ticket_price || 0) +
                        orderForm.purchased_upgrades * (gameAvailability.rules.vip_upgrade_price || 0)
                      ).toFixed(2)} €
                    </span>
                  </div>
                  {orderForm.free_tickets > 0 && (
                    <div className="flex justify-between text-gray-500 text-xs mt-2">
                      <span>Vom Saison-Kontingent:</span>
                      <span>-{orderForm.free_tickets} Tickets, -{orderForm.free_upgrades} Upgrades</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setShowOrderDialog(false)}>
                  Abbrechen
                </Button>
                <Button 
                  onClick={handleOrder}
                  className="bg-[#e30613] hover:bg-[#c00510]"
                  disabled={orderForm.free_tickets + orderForm.purchased_tickets === 0}
                >
                  Bestellung bestätigen
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Bestellhistorie
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {orders.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Keine Bestellungen vorhanden</p>
            ) : (
              orders.map((order) => (
                <Card key={order.id} className={order.is_cancelled ? 'opacity-60' : ''}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold">{order.event_title}</h4>
                        <p className="text-sm text-gray-500">{formatDate(order.event_date)}</p>
                      </div>
                      {order.is_cancelled ? (
                        <Badge variant="destructive">Storniert</Badge>
                      ) : (
                        <Badge className="bg-green-500">Aktiv</Badge>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                      <div>
                        <span className="text-gray-500">Kostenlos:</span>
                        <span className="ml-2">{order.free_tickets} Tickets, {order.free_upgrades} Upgrades</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Gekauft:</span>
                        <span className="ml-2">{order.purchased_tickets} Tickets, {order.purchased_upgrades} Upgrades</span>
                      </div>
                    </div>
                    
                    <div className="mt-2 text-sm">
                      <span className="text-gray-500">Lieferart:</span>
                      <span className="ml-2">
                        {order.delivery_type === 'fach' ? 'Ins Fach' : 
                         order.delivery_type === 'digital' ? 'Digital' : 
                         `Hinterlegt für ${order.hinterlegt_vorname} ${order.hinterlegt_nachname}`}
                      </span>
                    </div>
                    
                    {order.borrowed_from?.length > 0 && (
                      <div className="mt-2 text-sm">
                        <span className="text-gray-500">Geliehen von:</span>
                        <span className="ml-2">
                          {order.borrowed_from.map(b => 
                            `${b.from_user_name} (${b.tickets_borrowed}T/${b.upgrades_borrowed}U)`
                          ).join(', ')}
                        </span>
                      </div>
                    )}
                    
                    <p className="text-xs text-gray-400 mt-2">
                      Bestellt am {formatDate(order.created_at)}
                    </p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tickets;
